import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { DEVICE_HEARTBEAT_INTERVAL_MS } from '@shripos/shared/constants';
import { useOffline } from '../stores/offline.js';
import { useSession } from '../stores/session.js';
import { API_ORIGIN, APP_VERSION, api, logout, queryClient, reloadMe } from './api.js';
import { scheduleHoldsRefresh } from './offline/holds.js';
import { devicePrinterStatuses } from './printing/devices.js';

/**
 * Live updates over Socket.IO while signed in: license changes, announcements,
 * notifications, device revocation and remote sign-out.
 */
export function useRealtime(enabled) {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const socket = io(`${API_ORIGIN}/kiosk`, {
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', async (err) => {
      setConnected(false);
      // Access token expired between requests: refresh, then reconnect.
      if (['TOKEN_EXPIRED', 'AUTH_REQUIRED'].includes(err.message) && (await api.refresh())) socket.connect();
    });
    socket.on('license:updated', () => {
      reloadMe();
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    });
    // The outlet changed (e.g. its kitchen switch): the KOT button and kitchen screen follow.
    socket.on('outlet:updated', () => {
      reloadMe();
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    });
    socket.on('announcement:changed', () => queryClient.invalidateQueries({ queryKey: ['announcements'] }));
    socket.on('notification:new', () => queryClient.invalidateQueries({ queryKey: ['notifications'] }));
    // Menu/price/stock/settings changes from any device refresh the POS catalog at once.
    socket.on('menu:updated', () => queryClient.invalidateQueries({ queryKey: ['catalog'] }));
    socket.on('settings:updated', () => queryClient.invalidateQueries({ queryKey: ['catalog'] }));
    // Printer setup / health changes (agents connecting, paper out, …).
    socket.on('printers:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['printers'] });
      queryClient.invalidateQueries({ queryKey: ['print-agents'] });
    });
    socket.on('printers:status', () => queryClient.invalidateQueries({ queryKey: ['printers'] }));
    socket.on('shift:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['shift-current'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['day-status'] });
    });
    socket.on('bill:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['today'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      scheduleHoldsRefresh(); // this device's copy of the open orders, for working offline
    });
    // Kitchen display and self-order kiosks.
    socket.on('kot:new', () => queryClient.invalidateQueries({ queryKey: ['kds'] }));
    socket.on('kot:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['kds'] });
      queryClient.invalidateQueries({ queryKey: ['bills', 'held'] });
    });
    socket.on('selforder:new', (e) => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      scheduleHoldsRefresh();
      window.dispatchEvent(new CustomEvent('shripos:selforder', { detail: e }));
    });
    socket.on('session:revoked', ({ sessionId }) => {
      if (sessionId === useSession.getState().me?.session?.id) {
        queryClient.clear();
        useSession.getState().signedOut('sessionEnded');
      }
    });
    socket.on('device:revoked', () => {
      useSession.getState().setDevice(null);
      logout().then(() => useSession.getState().signedOut('deviceRevoked'));
    });
    return () => {
      socket.disconnect();
      setConnected(false);
    };
  }, [enabled]);
  return connected;
}

/** Registered devices report in every minute (app version, health of printers plugged into them). */
export function useHeartbeat() {
  const deviceId = useSession((s) => s.device?.device?.id);
  useEffect(() => {
    if (!deviceId) return undefined;
    const beat = () =>
      api
        .post('/devices/heartbeat', {
          appVersion: APP_VERSION,
          printerStatus: devicePrinterStatuses(),
          // Bills waiting to sync on this device (shown to managers in Devices).
          outbox: { pending: useOffline.getState().pending, failed: useOffline.getState().failed },
        })
        .catch((err) => {
          if (err.code === 'DEVICE_REVOKED' || err.code === 'DEVICE_REQUIRED') {
            useSession.getState().setDevice(null);
            if (useSession.getState().me?.device) logout().then(() => useSession.getState().signedOut('deviceRevoked'));
          }
        });
    beat();
    const t = setInterval(beat, DEVICE_HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(t);
  }, [deviceId]);
}

export function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
